import styled, { css, keyframes } from 'styled-components';
import { MenuTitle } from '../MainMenu/mainMenu.styles';
import { repeatShadow, vh } from '../styles/utils';
import { ANIMATION_DURATION, MAX_DROPS } from '../lib/constants';
import { DotValue } from '../lib/types';

export const GameContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const GameContainerInner = styled.div`
  display: inline-block;
  position: relative;
  transform: translateZ(0.1);
`;

type GameMetaProps = {
  tutorial: boolean;
};

export const GameMeta = styled.table<GameMetaProps>`
  position: absolute;
  th, td {
    font-weight: normal;
    text-align: left;
    font-size: ${vh(3)};
    padding-right: ${vh(1)};
    opacity: ${(props) => props.tutorial ? 0 : 1};
  }
`;

export const GameHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${vh(3)};
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${vh(2)};
`;

export const Score = styled.h1`
  margin: 0;
  font-weight: normal;
  font-size: ${vh(9)};
  line-height: 0.75;
  color: #ddd;
  text-align: right;
  flex-grow: 1;
`;

type BoardContainerProps = {
  $disabled: boolean;
  tutorial: boolean;
  shadePieces: boolean;
};

export const BoardContainer = styled.div<BoardContainerProps>`
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  display: grid;
  grid-template-columns: repeat(7, ${vh(10.5)});
  cursor: ${(props) => props.$disabled ? 'default' : 'pointer'};

  ${(props) => props.tutorial && css`
    margin-top: ${vh(10)};
  `}

  ${() => css`
    ${Piece} {
      transition: opacity ${ANIMATION_DURATION / 3}ms ease-in-out;
    }
  `}
  
  ${(props) => props.shadePieces && css`
    ${Piece} {
      opacity: 0.5;
    }
  `}
`;

const Piece = styled.div`
  box-sizing: border-box;
  width: ${vh(9)};
  height: ${vh(9)};
  border-radius: 999rem;
`;

export const SolidPiece = styled(Piece)`
  position: relative;
  border: solid ${vh(1.6)} #ccc;
  ::before {
    content: '';
    position: absolute;
    box-sizing: border-box;
    top: ${vh(-0.8)};
    left: ${vh(-0.8)};
    right: ${vh(-0.8)};
    bottom: ${vh(-0.8)};
    border-radius: 999rem;
    background-color: #ccc;
    border: solid ${vh(0.9)} #000;
  }
`;

const bgHighlight = keyframes`
  0% {
    background-color: #000;
  }
  75% {
    background-color: #666;
  }
  100% {
    background-color: #666;
  }
`;

const borderHighlight = keyframes`
  0% {
    border-color: #000;
  }
  75% {
    border-color: #666;
  }
  100% {
    border-color: #666;
  }
`;

const shrink = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
`;

type CellProps = {
  highlighted?: boolean;
  $value?: DotValue;
};

export const Cell = styled.div<CellProps>`
  position: relative;
  background-color: #000;
  border-bottom: solid 1px #999;
  border-right: solid 1px #999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  
  animation: none;

  ${SolidPiece}::before {
    animation: none;
  }
  
  ${(props) => props.highlighted && css`
    animation: ${ANIMATION_DURATION}ms ${bgHighlight} ease-out;
    animation-fill-mode: forwards;

    ${SolidPiece}::before {
      animation: ${ANIMATION_DURATION}ms ${borderHighlight} ease-out;
      animation-fill-mode: forwards;
    }
  `}
`;

const cellScore = keyframes`
  0% {
    opacity: 1;
    margin-top: ${vh(2)};
    transform: scale(0);
  }
  50% {
    opacity: 1;
    margin-top: ${vh(-3)};
    transform: scale(1);
  }
  100% {
    opacity: 1;
    margin-top: ${vh(-3)};
    transform: scale(1);
  }
`;

const cellScoreShadow = repeatShadow('#000', 3);

type CellScoreProps = {
  $value: DotValue;
};

export const CellScore = styled.div<CellScoreProps>`
  position: absolute;
  top: 50%;
  bottom: 50%;
  left: 0;
  right: 0;
  z-index: 1;
  font-size: ${vh(4)};
  font-weight: bold;
  pointer-events: none;
  color: rgb(${(props) => colors[props.$value ?? 1].join(', ')});
  text-align: center;
  line-height: 0;
  text-shadow: ${cellScoreShadow};
  opacity: 0;
  animation: ${ANIMATION_DURATION / 3}ms ${cellScore} ease-out;
  animation-delay: ${ANIMATION_DURATION * 5 / 9}ms;
  animation-fill-mode: forwards;
`;

export const DropCell = styled(Cell)`
  background-color: transparent !important;
  border-top: none;
  border-left: none !important;
  border-right: none;

  ${SolidPiece}::before {
    border-color: #666 !important;
  }
`;

export const CrackedPiece = styled(SolidPiece)`
  border-style: dotted;
`;

type ColumnContainerProps = {
  hovered: boolean;
  $allowed: boolean;
  $required: boolean;
  $highlightSolids: boolean;
};

export const ColumnContainer = styled.div<ColumnContainerProps>`
  position: relative;
  display: grid;
  grid-template-rows: repeat(8, ${vh(10.5)});
  :first-child ${Cell} {
    border-left: solid 1px #999;
  }
  ${(props) => props.hovered && css`
    ${Cell} {
      background-color: ${props.$allowed ? '#333' : '#191919'};
    }
    
    ${SolidPiece}::before {
      border-color: #333;
    }
  `}

  ${(props) => props.$required && css`
    &::before {
      content: '';
      position: absolute;
      top: ${vh(10.5)};
      bottom: 0;
      left: 0;
      right: 0;
      margin: -2px -1px -1px -2px;
      border: solid 2px #fff;
      z-index: 1;
    }

    ${Piece} {
      opacity: 1;
    }
  `}

  ${(props) => props.$highlightSolids && css`
    ${SolidPiece} {
      opacity: 1;
    }
  `}
`;

const colors = {
  1: [103, 156, 67],
  2: [199, 198, 65],
  3: [200, 127, 55],
  4: [200, 60, 47],
  5: [173, 79, 147],
  6: [90, 185, 215],
  7: [60, 72, 133],
} as const;

const pieceShadow = repeatShadow('#000', 35);

type NumberPieceProps = {
  $value: DotValue;
  score: number;
};

export const NumberPiece = styled(Piece)<NumberPieceProps>`
  position: relative;
  font-size: ${vh(7)};
  line-height: 1.05;
  font-weight: bold;
  overflow: hidden;
  background-color: rgb(${(props) => colors[props.$value].join(', ')});
  text-align: center;
  text-shadow: ${pieceShadow};
  padding-top: ${vh(0.6)};
  padding-left: ${vh(0.6)};
  
  animation: none;
  
  ${(props) => props.score > 0 && css`
    animation: ${shrink} ease-out;
    animation-duration: ${ANIMATION_DURATION * 0.9}ms;
    animation-delay: ${ANIMATION_DURATION / 6}ms;
    animation-fill-mode: forwards;
  `}
`;

export const Drops = styled.div`
  display: grid;
  grid-template-columns: repeat(${MAX_DROPS}, 1fr);
  margin-top: ${vh(2)};
  margin-bottom: ${vh(2)};
`;

export const Drop = styled.div<{ unused: boolean }>`
  width: ${vh(1.7)};
  height: ${vh(1.7)};
  border-radius: 999rem;
  background-color: ${(props) => props.unused ? '#eee' : '#999' };
`;

export const Level = styled.h2`
  margin: 0;
  font-size: ${vh(3)};
  font-weight: normal;
  text-align: left;
  text-transform: uppercase;
`;

export const MenuContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: ${vh(6)};
  align-items: center;
  justify-content: center;
  z-index: 5;
`;

const titleShadow = repeatShadow('#555', 12);

export const ModeLabel = styled(MenuTitle)`
  text-transform: none;
  font-size: ${vh(12)};
  text-shadow: ${titleShadow};
`;

const gameOver = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

export const GameOverContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.85);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: ${ANIMATION_DURATION / 2}ms ${gameOver} ease-in;
  animation-fill-mode: forwards;
`;

const gameOverShadow = repeatShadow('#555', 10);

export const GameOverTitle = styled.h1`
  text-transform: uppercase;
  font-size: ${vh(10)};
  font-weight: bold;
  letter-spacing: 0;
  line-height: 1;
  word-spacing: ${vh(200)};
  margin: 0 0 ${vh(4)};
  text-shadow: ${gameOverShadow};
`;

export const GameOverScoreHeader = styled.h2`
  font-size: ${vh(4)};
  font-weight: normal;
  margin: 0;
`;

export const GameOverScore = styled.h3`
  font-size: ${vh(7)};
  font-weight: normal;
  margin-top: 0;
  margin-bottom: ${vh(6)};
  text-transform: uppercase;
`;

export const GameOverTable = styled.table`
  font-size: ${vh(4)};
  margin-bottom: ${vh(6)};
  th, td {
    font-weight: normal;
    width: 50%;
    padding: 0 ${vh(1)};
  }
  th {
    text-align: right;
  }
  td {
    text-align: left;
  }
`;

const AverageArrow = styled.div`
  display: inline-block;
  &::before {
    position: relative;
    margin-left: ${vh(2)};
    content: '';
    width: 0;
    height: 0;
    border-left: solid ${vh(2.2)} transparent;
    border-right: solid ${vh(2.2)} transparent;
  }
`;

export const AverageIncrease = styled(AverageArrow)`
  &::before {
    top: ${vh(-3.6)};
    border-bottom: solid ${vh(2.8)} rgb(103, 156, 67);
  }
`;

export const AverageDecrease = styled(AverageArrow)`
  &::before {
    top: ${vh(3.6)};
    border-top: solid ${vh(2.8)} rgb(200, 60, 47);
  }
`;

export const GameOverButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vh(4)};
`;

type ComboProps = {
  tutorial: boolean;
};

const combo = keyframes`
  0% {
    opacity: 1;
    margin-top: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    margin-top: ${vh(-1)};
    transform: scale(1);
  }
`;

export const Combo = styled.h2<ComboProps>`
  position: absolute;
  top: ${(props) => props.tutorial ? vh(20) : vh(12)};
  left: 0;
  right: 0;
  margin: 0;
  font-size: ${vh(5)};
  font-weight: normal;
  text-transform: uppercase;
  flex-grow: 1;
  text-align: center;
  align-self: flex-end;
  animation: ${ANIMATION_DURATION / 2}ms ${combo} ease-out;
  animation-fill-mode: forwards;
`;

const overlayTextShadow = repeatShadow('#555', 10);

type BonusTextProps = {
  visible: boolean;
  shadePieces: boolean;
  tutorial: boolean;
};

export const BonusText = styled.h2<BonusTextProps>`
  position: absolute;
  z-index: 2;
  top: ${(props) => props.tutorial ? vh(28) : vh(20)};
  left: 0;
  right: 0;
  font-size: ${vh(5.5)};
  font-weight: bold;
  margin: 0;
  text-align: center;
  text-transform: uppercase;
  text-shadow: ${overlayTextShadow}, 2px 2px #555, 2px -2px #555, -2px -2px #555;
  pointer-events: none;
  opacity: 0;
  transition: opacity ${ANIMATION_DURATION / 3}ms ease-out;
  ${(props) => props.visible && css`
    opacity: ${props.shadePieces ? 0.6 : 1};
  `}
`;

export const PauseButton = styled.button`
  position: relative;
  z-index: 2;
  background-color: transparent;
  border: solid ${vh(0.5)} #ccc;
  margin-right: ${vh(1)};
  width: ${vh(7)};
  height: ${vh(7)};
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 999rem;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  :active,
  :focus {
    background-color: #999;
  }

  :disabled {
    background-color: transparent;
    cursor: default;
  }

  &::before,
  &::after {
    content: '';
    background-color: #ccc;
    position: absolute;
    top: ${vh(1.6)};
    bottom: ${vh(1.6)};
    width: ${vh(0.6)};
  }

  &::before {
    margin-left: ${vh(-1.8)};
  }

  &::after {
    margin-right: ${vh(-1.8)};
  }
`;

export const TutorialOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  cursor: pointer;
`;

type TutorialBubbleProps = {
  visible: boolean;
};

export const TutorialBubble = styled.div<TutorialBubbleProps>`
  position: absolute;
  top: ${vh(-6)};
  left: 0;
  right: ${vh(10)};
  height: ${vh(18)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${vh(2)};
  background-color: rgb(60, 72, 133);
  z-index: 3;
  font-size: ${vh(3.5)};
  padding: ${vh(2)};
  border-radius: ${vh(2)};
  pointer-events: none;
  transition: opacity ${ANIMATION_DURATION / 3}ms ease-in-out;
  opacity: ${(props) => props.visible ? 1 : 0};
  
  p {
    margin: 0;
  }
`;
